import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthLoginComponent } from '@app/core/auth/auth-login/auth-login.component';
import { RedirectsService } from '@app/core/services/redirect/redirects.service';
import { StorageService } from '@app/core/services/storage/storage.service';
import { TrackingEventType } from '@app/core/services/tracking/config/tracking-map';
import { ConsentsFormComponent } from '@app/shared/components/consents-form/consents-form.component';
import { LoaderComponent } from '@app/shared/components/loader/loader.component';
import { WINDOW } from '@app/shared/providers/window.provider';
import { TranslateModule } from '@ngx-translate/core';
import {
  PhoneNumber,
  RCAutocompleteItem,
  RCAutocompleteModule,
  RCButtonModule,
  RCFieldModule,
  RCIconModule,
  RCPhoneFieldModule,
} from '@rc/ui';

import { iif, of, Subject, switchMap } from 'rxjs';
import { catchError, map, take, takeUntil, tap } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/core/environment.service';
import {
  CountryConsentFormGroup,
  Credentials,
  ErrorContext,
  Password,
  Profile,
  RegisterUser,
  RegisterUserResponse,
  STORAGE_KEY_ERROR,
} from 'src/app/core/models';
import { LocaleService } from 'src/app/core/services';
import { CountriesService } from 'src/app/core/services/countries/countries.service';
import { MarsPrivacyPolicyService } from 'src/app/core/services/mars-privacy-policy.service';
import { RegisterService } from 'src/app/core/services/register.service';
import { findLanguage } from 'src/app/core/services/tool.helper';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { getFormattedConsents } from 'src/app/core/services/utils/consents/consents.utils';
import { Languages } from 'src/app/shared/utils';
import { LoginType } from 'src/app/shared/utils/enums/login-type.enum';
import { phoneRequired, ValidatorPropName } from 'src/app/shared/validators';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

@Component({
  selector: 'app-register-page-form',
  templateUrl: './register-page-form.component.html',
  styleUrls: ['./register-page-form.component.scss'],
  imports: [
    CommonModule,
    LoaderComponent,
    AuthLoginComponent,
    FormsModule,
    ReactiveFormsModule,
    RCFieldModule,
    RCAutocompleteModule,
    RCPhoneFieldModule,
    TranslateModule,
    RCButtonModule,
    ConsentsFormComponent,
    RCIconModule,
  ],
})
export class RegisterPageFormComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private registerService = inject(RegisterService);
  private router = inject(Router);
  private localeService = inject(LocaleService);
  private countriesService = inject(CountriesService);
  private storageService = inject(StorageService);
  private trackingService = inject(TrackingService);
  private marsPrivacyPolicyService = inject(MarsPrivacyPolicyService);
  environmentService = inject(EnvironmentService);
  private window = inject<Window>(WINDOW);
  private redirectsService = inject(RedirectsService);
  private activatedRoute = inject(ActivatedRoute);

  registerForm!: FormGroup<{
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    countryLabel: FormControl<string | null>;
    password: FormControl<string | null>;
    passwordConfirm: FormControl<string | null>;
    primaryPhone: FormControl<PhoneNumber | null>;
    email: FormControl<string | null>;
    consents: CountryConsentFormGroup;
  }>;

  firstNameControlError = signal('');
  lastNameControlError = signal('');
  countryLabelControlError = signal('');
  passwordConfirmControlError = signal('');
  emailControlError = signal('');

  get queryParams() {
    return this.activatedRoute.snapshot.queryParams;
  }
  clientId = this.queryParams.client;

  showPassword = false;
  showPasswordConfirm = false;
  showPopover = false;
  submitted = false;
  countryLabels: RCAutocompleteItem<string>[] = [];
  loginType: LoginType = LoginType.email;
  readonly loginTypeEnum = LoginType;
  // i18n keys for form rules
  private formRulesKeys = [
    'fieldRules_required',
    'fieldRules_same',
    'fieldRules_email',
    'setPassword_rules1',
    'setPassword_rules2',
    'setPassword_rules3',
    'setPassword_rules4',
    'setPassword_rules5',
    'invalid_country_name',
    'FieldError.minLength',
    'FieldError.maxLength',
    'fieldRules_incorrectCharacter',
  ];

  // Validation message in case of error in form
  validationMessages!: {
    [key: string]: { type: string; message: string }[];
  };
  private _destroyed$ = new Subject<void>();

  constructor() {
    // Get form rules translation
    this.localeService
      .translateGet(this.formRulesKeys)
      .subscribe((translations) => {
        if (translations) {
          this.validationMessages = {
            firstName: [
              { type: 'required', message: translations.fieldRules_required },
              {
                type: 'incorrectFormat',
                message: translations.fieldRules_incorrectCharacter,
              },
              {
                type: 'minlength',
                message: translations['FieldError.minLength'],
              },
              {
                type: 'maxlength',
                message: translations['FieldError.maxLength'],
              },
            ],
            lastName: [
              { type: 'required', message: translations.fieldRules_required },
              {
                type: 'incorrectFormat',
                message: translations.fieldRules_incorrectCharacter,
              },
            ],
            email: [
              { type: 'required', message: translations.fieldRules_required },
              { type: 'email', message: translations.fieldRules_email },
            ],
            countryLabel: [
              { type: 'required', message: translations.fieldRules_required },
              {
                type: 'notIncluded',
                message: translations.invalid_country_name,
              },
            ],
            password: [
              { type: 'required', message: translations.fieldRules_required },
              { type: 'hasNumber', message: translations.setPassword_rules4 },
              {
                type: 'hasCapitalCase',
                message: translations.setPassword_rules3,
              },
              {
                type: 'hasSmallCase',
                message: translations.setPassword_rules2,
              },
              {
                type: 'hasSpecialCharacters',
                message: translations.setPassword_rules5,
              },
              { type: 'minlength', message: translations.setPassword_rules1 },
            ],
            passwordConfirm: [
              { type: 'required', message: translations.fieldRules_required },
              {
                type: 'NoPassswordMatch',
                message: translations.fieldRules_same,
              },
            ],
          };
        }
      });
  }

  ngOnInit(): void {
    [this.loginType] = this.environmentService.getLoginType();
    this.getCountries();
    this.buildRegisterForm();
  }
  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  goBack(): void {
    this.redirectsService
      .getSecuredGoBackUri(this.queryParams)
      .pipe(take(1))
      .subscribe((origin) => {
        this.window.location.href = origin
          ? this.redirectsService.addQueryParamsToUrl(origin, {
              origin: 'cancel',
            })
          : this.environmentService.getGlobalRedirectURI();
      });
  }

  popoverOpen(): void {
    this.showPopover = true;
  }
  popoverClose(): void {
    this.showPopover = false;
  }

  /**
   * Format the name to have the first letter of each word in uppercase
   * and the rest of the name in lowercase
   * @param {string} name
   * @returns {string} The name formatted
   */
  formatName(name: string): string {
    return name
      ?.trim()
      .replace(/\b[a-z]/g, (c) => c.toLocaleUpperCase())
      .replace(/\B[A-Z]/g, (c) => c.toLocaleLowerCase());
  }

  onSubmit(): void {
    if (!this.registerForm.valid || this.submitted) {
      return;
    }

    this.submitted = true;

    this.redirectsService
      .getSecuredCallbackUri(this.queryParams)
      .pipe(
        take(1),
        map((callbackUri) => {
          if (callbackUri) {
            return {
              registerUser: this.buildRegisterUser(callbackUri),
              callbackUri,
            };
          } else {
            throw new Error(`Invalid callbackUri`);
          }
        }),
        switchMap(({ registerUser, callbackUri }) => {
          return this.registerService
            .createUser(registerUser, callbackUri, this.clientId)
            .pipe(
              take(1),
              catchError((err) => {
                this.submitted = false;
                this.trackingService.pushEvent('registration.continueError');
                this.storeErrorInLocalStorage(registerUser, callbackUri, err);
                throw new Error(err);
              }),
              switchMap((response: RegisterUserResponse) => {
                const consentsValue =
                  this.registerForm.controls.consents.value!;
                const filteredConsents = Object.fromEntries(
                  Object.entries(consentsValue).filter(
                    ([_, v]) => typeof v === 'boolean',
                  ),
                ) as {
                  [purposeId: string]: boolean;
                };

                const formattedConsents = getFormattedConsents(
                  filteredConsents,
                  this.environmentService.getCountryConsentsWithPurposes(
                    this.registerForm.controls.countryLabel.value!,
                  ),
                );

                const addConsents$ = this.registerService
                  .addConsents(response.id, formattedConsents)
                  .pipe(map(() => response));

                return iif(
                  () => formattedConsents.length > 0,
                  addConsents$,
                  of(response),
                );
              }),
              tap((response: RegisterUserResponse) => {
                this.addRegistrationSuccessTracking();
                this.redirectOnSuccess(registerUser, callbackUri, response);
              }),
            );
        }),
        catchError((err) => {
          console.error('Error during registration:', err);
          this.router.navigate(['/error'], { queryParamsHandling: 'preserve' });
          return of();
        }),
      )
      .subscribe();
  }

  redirectTo(uri: string): void {
    this.window.location.href = uri;
  }

  /**
   * Find the country and region for the user profile.
   * The country should be the one selected in the registration form.
   * The region should be the one defined in the browser language.
   */
  private findLanguageAndLocale(selectedCountryCode: string) {
    const navigatorLanguage = this.localeService.getLanguage();
    const language =
      findLanguage(selectedCountryCode, navigatorLanguage.split('-')[0]) ||
      Languages.enUS;
    const locale = language.replace('-', '_');
    return { language, locale };
  }

  private buildRegisterForm() {
    const specialCharactersRegex =
      /[!@#$%^&*()~_¥¨`+§£°€\=\[\]{};:"\\|,.<>\/?0-9]+/;
    this.registerForm = this.formBuilder.group(
      {
        firstName: this.formBuilder.control<string | null>(null, [
          Validators.required,
          CustomValidators.noBlankString,
          Validators.maxLength(50),
          Validators.minLength(2),
          CustomValidators.patternValidator(
            specialCharactersRegex,
            { incorrectFormat: true },
            false,
          ),
        ]),
        lastName: this.formBuilder.control<string | null>(null, [
          Validators.required,
          CustomValidators.noBlankString,
          Validators.maxLength(50),
          CustomValidators.patternValidator(
            specialCharactersRegex,
            { incorrectFormat: true },
            false,
          ),
        ]),
        countryLabel: this.formBuilder.control<string | null>(null, {
          validators: [Validators.required],
        }),
        password: this.formBuilder.control<string | null>(
          null,
          Validators.compose([
            Validators.required,
            // check whether the entered password has a number
            CustomValidators.patternValidator(/\d/, { hasNumber: true }, true),
            // check whether the entered password has upper case letter
            CustomValidators.patternValidator(
              /[A-Z]/,
              { hasCapitalCase: true },
              true,
            ),
            // check whether the entered password has a lower-case letter
            CustomValidators.patternValidator(
              /[a-z]/,
              { hasSmallCase: true },
              true,
            ),
            // check whether the entered password has a special character
            CustomValidators.patternValidator(
              /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
              {
                hasSpecialCharacters: true,
              },
              true,
            ),
            // Has a minimum length of 8 characters
            Validators.minLength(8),
          ]),
        ),
        passwordConfirm: this.formBuilder.control<string | null>(
          null,
          Validators.required,
        ),
        primaryPhone: this.formBuilder.control<PhoneNumber | null>(
          {
            value: {
              phone: '',
              countryCode:
                this.localeService.getCountry() as PhoneNumber['countryCode'],
            },
            disabled: this.loginType === LoginType.email,
          },
          [phoneRequired()],
        ),
        email: this.formBuilder.control<string | null>(
          { value: null, disabled: this.loginType === LoginType.primaryPhone },
          [Validators.email, Validators.required],
        ),
        consents: this.formBuilder.group<Record<string, FormControl<boolean>>>(
          {},
        ),
      },
      {
        // check whether our password and confirm password match
        validators: CustomValidators.passwordMatchValidator,
      },
    );

    this.plugErrorHandlerToFormControl(
      this.registerForm.controls.firstName,
      this.validationMessages.firstName,
      this.firstNameControlError,
    );
    this.plugErrorHandlerToFormControl(
      this.registerForm.controls.lastName,
      this.validationMessages.lastName,
      this.lastNameControlError,
    );
    this.plugErrorHandlerToFormControl(
      this.registerForm.controls.countryLabel,
      this.validationMessages.countryLabel,
      this.countryLabelControlError,
    );
    this.plugErrorHandlerToFormControl(
      this.registerForm.controls.passwordConfirm,
      this.validationMessages.passwordConfirm,
      this.passwordConfirmControlError,
    );
    this.plugErrorHandlerToFormControl(
      this.registerForm.controls.email,
      this.validationMessages.email,
      this.emailControlError,
    );

    this.registerForm.controls.password.valueChanges.pipe(
      takeUntil(this._destroyed$),
      tap(() =>
        this.registerForm.controls.passwordConfirm.updateValueAndValidity(),
      ),
    );
    this.registerForm.controls.countryLabel.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        tap((countryCode) => {
          this.marsPrivacyPolicyService.setCountryCode(
            countryCode || undefined,
          );

          if (this.loginType === LoginType.primaryPhone) {
            const primaryPhoneControl = this.registerForm.controls.primaryPhone;

            if (!primaryPhoneControl.value?.phone) {
              primaryPhoneControl.patchValue({
                phone: '',
                countryCode:
                  (countryCode as PhoneNumber['countryCode']) ?? undefined,
              });
              primaryPhoneControl.markAsUntouched();
            } else {
              primaryPhoneControl.updateValueAndValidity();
            }
          }
        }),
      )
      .subscribe();
  }

  private plugErrorHandlerToFormControl(
    control: AbstractControl,
    errors: { type: string; message: string }[],
    controlError: WritableSignal<string>,
  ): void {
    control.statusChanges
      .pipe(takeUntil(this._destroyed$))
      .subscribe((status) => {
        if (status === 'INVALID') {
          const error = this.getFormControlError(control, errors);
          controlError.set(error);
        } else {
          controlError.set('');
        }
      });
  }

  private getFormControlError(
    control: AbstractControl,
    errors: { type: string; message: string }[],
  ): string {
    const error = errors.find((error) => control.hasError(error.type));
    return error ? error.message : '';
  }

  private getCountries() {
    this.countriesService
      .get(this.localeService.getLanguage())
      .subscribe((countries) => {
        this.countryLabels = countries.map((val) => ({
          label: val.label,
          value: val.code,
        }));
        const countriesCodes = this.countryLabels.map((a) => a.value);
        this.addCountryLabelValidators(countriesCodes);
      });
  }

  private addCountryLabelValidators(countries: string[]) {
    this.registerForm.controls.countryLabel.setValidators([
      Validators.required,
      CustomValidators.includes(countries),
    ]);
  }

  /**
   * Manage input error messages
   * @param control
   * Contains the control of which we will pick up errors
   */
  getFirstErrorControl(control: AbstractControl | null): string {
    const errors = control?.errors;
    if (!control || !errors || control.untouched) {
      return '';
    }
    const firstKeyError = errors[0] || null;
    if (firstKeyError) {
      switch (firstKeyError) {
        case ValidatorPropName.phoneNumber:
          return 'fieldRules_PhoneNumber';
        case ValidatorPropName.required:
          return control?.touched ? 'fieldRules_required' : '';
        case ValidatorPropName.incorrect:
          return 'registerApp_fieldPrimaryPhoneError';
      }
    }
    return '';
  }

  private storeErrorInLocalStorage(
    userToCreate: RegisterUser,
    callbackUri: string,
    err: any,
  ) {
    this.storageService.setSession(STORAGE_KEY_ERROR, {
      context: ErrorContext.REGISTER,
      summary: this.localeService.translateInstant(
        'registerApp_errorPageRegisterSummary',
      ),
      details: this.localeService.translateInstant(
        'registerApp_errorPageDetails',
      ),
      clientId: this.clientId,
      apiCodeError: err?.status ?? '',
      apiUrlError: err?.url ?? '',
      apiErrorReturn: err?.error?.message ?? err?.message ?? '',
      redirectUri: callbackUri,
      userLocale: userToCreate?.locale ?? '',
    });
  }

  /**
   * If we receive a sessionToken it means that email confirmation is disabled in okta, and we redirect to the callback Url
   * Else we redirect to the registration success page
   * @param userToCreate
   * @param callbackUri
   * @param response
   * @private
   */
  private redirectOnSuccess(
    userToCreate: RegisterUser,
    callbackUri: string,
    response: RegisterUserResponse,
  ) {
    if (response.sessionToken) {
      this.redirectTo(
        this.redirectsService.addQueryParamsToUrl(callbackUri, {
          origin: 'register',
          token: response.sessionToken,
        }),
      );
    } else {
      this.router.navigate(
        [
          this.loginType === LoginType.email
            ? 'register/email/success'
            : 'register/primary-phone/success',
        ],
        {
          queryParams: { client: this.clientId },
          state: { data: userToCreate, user_id: response.id },
        },
      );
    }
  }

  private addRegistrationSuccessTracking() {
    this.trackingService.pushEvent(
      {
        [LoginType.email]: 'registration.continueSuccessEmail',
        [LoginType.primaryPhone]: 'registration.continueSuccessPhone',
        [LoginType.username]: 'registration.continueSuccessUsername',
      }[this.loginType] as TrackingEventType,
    );
  }

  private buildRegisterUser(callbackUri: string): RegisterUser {
    // at this point, the form expected to be valid
    const formData = this.registerForm.value!;
    const { language, locale } = this.findLanguageAndLocale(
      formData.countryLabel!,
    );
    const login =
      this.loginType === LoginType.email
        ? formData.email!.trim()
        : `${formData.primaryPhone!.phone}@phonenumber.com`;
    // User profile object
    const userProfile: Profile = {
      firstName: this.formatName(formData.firstName!),
      lastName: this.formatName(formData.lastName!),
      email: login,
      login: login,
      locale,
      primaryPhone: formData?.primaryPhone?.phone,
      preferredLanguage: language,
      countryCode: formData.countryLabel!,
    };
    // Credential object
    const passwordCredential: Password = {
      value: formData.password!,
    };
    const userCredentials: Credentials = {
      password: passwordCredential,
    };
    // Build the final object to send to API
    return {
      callback: callbackUri,
      locale,
      profile: userProfile,
      credentials: userCredentials,
    };
  }
}
